import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { api, type PlaceAddress, type PlaceSuggestion } from '../../lib/api';

/**
 * An address box that suggests as you type.
 *
 * Picking a suggestion resolves it to a position on the spot, so whatever this
 * feeds needs no geocode afterwards. Typing over a picked address drops the
 * position again -- the text and the pin must never describe two places.
 *
 * One session token per typing run, ended by the pick: that is what makes
 * Google bill the run as one lookup instead of one per keystroke.
 */

export interface PickedAddress {
  location: { lat: number; lng: number };
  formatted: string;
  address: PlaceAddress;
}

const DEBOUNCE_MS = 250;

function newSession(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function AddressAutocomplete({
  territoryId,
  value,
  onChange,
  onPick,
  placeholder,
}: {
  territoryId: string | null;
  value: string;
  /** Every keystroke. The pick, if any, is void from here on. */
  onChange: (text: string) => void;
  onPick: (picked: PickedAddress) => void;
  placeholder?: string;
}) {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const session = useRef(newSession());
  /* Only a keystroke asks for suggestions; a pick setting the text must not. */
  const typed = useRef(false);
  const latest = useRef(0);

  useEffect(() => {
    if (!typed.current || !territoryId) return;
    const q = value.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const ticket = ++latest.current;
    const timer = setTimeout(() => {
      api
        .placesAutocomplete(territoryId, q, session.current)
        .then((r) => {
          if (ticket !== latest.current) return;
          setNotice(r.available ? (r.error ?? null) : null);
          setSuggestions(r.suggestions);
          setActive(0);
          setOpen(r.suggestions.length > 0);
        })
        .catch(() => {
          if (ticket === latest.current) setNotice('Address suggestions are unavailable right now.');
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [value, territoryId]);

  const pick = async (s: PlaceSuggestion) => {
    if (!territoryId) return;
    setOpen(false);
    typed.current = false;
    latest.current++;
    onChange(s.text);
    setResolving(true);
    try {
      const r = await api.placesResolve(territoryId, s.placeId, session.current);
      onChange(r.formatted || s.text);
      onPick(r);
      setNotice(null);
    } catch {
      setNotice('Could not look up that address; it will be found when you search.');
    } finally {
      setResolving(false);
      /* The pick ends the session; the next typing run is a new one. */
      session.current = newSession();
    }
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      const s = suggestions[active];
      if (s) {
        e.preventDefault();
        void pick(s);
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <span className="addr-ac">
      <input
        type="text"
        className="field"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        onChange={(e) => {
          typed.current = true;
          onChange(e.target.value);
        }}
        onKeyDown={onKey}
        onFocus={() => setOpen(suggestions.length > 0 && typed.current)}
        /* Late enough that a click on a suggestion lands first. */
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {resolving ? <span className="spinner addr-ac-spin" /> : null}
      {open ? (
        <ul className="addr-ac-list" role="listbox">
          {suggestions.map((s, i) => (
            <li key={s.placeId} role="option" aria-selected={i === active}>
              <button
                type="button"
                data-active={i === active ? 'true' : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={() => void pick(s)}
              >
                <b>{s.main}</b>
                {s.secondary ? <span>{s.secondary}</span> : null}
              </button>
            </li>
          ))}
          <li className="addr-ac-credit">Powered by Google</li>
        </ul>
      ) : null}
      {notice ? <small className="addr-ac-note">{notice}</small> : null}
    </span>
  );
}
